import type { ChartInstance } from "../kline-helpers";
import { DEFAULT_DRAWING_COLOR } from "@/lib/chart-drawings/defaults";
import type { DrawingInstance, DrawingPoint } from "@/lib/chart-drawings/types";
import type { OnPointsChange } from "./types";

/**
 * KLineCharts maps `point.timestamp` → `dataIndex` via binary search through the
 * loaded candle data. For timestamps beyond the last candle this collapses to
 * the last index, so any future-anchored point gets clamped to the last candle
 * (e.g. a rectangle drawn from now-1h to now+1h would extend to infinity).
 *
 * To draw at future points correctly we extrapolate `dataIndex` ourselves from
 * the bar interval and supply it without a `timestamp` field — KLineCharts
 * gives priority to `point.dataIndex` only when `point.timestamp` is absent.
 *
 * Past/present points are passed through unchanged so KLineCharts can still
 * resolve them precisely against the data list.
 */
export type OverlayRenderPoint = {
  timestamp?: number;
  dataIndex?: number;
  value?: number;
  [key: string]: unknown;
};

interface ChartTiming {
  lastIndex: number;
  lastTs: number;
  barInterval: number;
}

function readChartTiming(
  chart: ChartInstance | null | undefined
): ChartTiming | null {
  if (!chart || typeof chart.getDataList !== "function") return null;
  let dataList: Array<{ timestamp: number }>;
  try {
    dataList = chart.getDataList() as Array<{ timestamp: number }>;
  } catch {
    return null;
  }
  if (!dataList?.length) return null;
  const lastIndex = dataList.length - 1;
  const lastTs = dataList[lastIndex].timestamp;
  const barInterval =
    dataList.length >= 2
      ? Math.max(1, lastTs - dataList[lastIndex - 1].timestamp)
      : 60_000;
  return { lastIndex, lastTs, barInterval };
}

/**
 * KLineCharts populates `dataIndex` for points dragged into the future but
 * leaves `timestamp` empty (because no candle exists at that future time).
 * If we persist as-is, the dataIndex is meaningless once the underlying data
 * list changes (symbol/timeframe switch, scroll-loading more history, etc.)
 * and the drawing's anchor is lost.
 *
 * This helper guarantees every persisted point carries an absolute wall-clock
 * `timestamp` by extrapolating from the chart's bar interval. Pure function:
 * if no synthesis is needed it returns the original array.
 */
export function synthesizeFutureTimestamps<T extends DrawingPoint>(
  chart: ChartInstance | null | undefined,
  points: readonly T[] | undefined
): T[] {
  if (!points?.length) return [];
  const timing = readChartTiming(chart);
  if (!timing) return [...points];

  let mutated = false;
  const next = points.map((p) => {
    const hasTs =
      typeof p.timestamp === "number" && Number.isFinite(p.timestamp);
    if (hasTs) return p;

    const dataIndex = (p as { dataIndex?: number }).dataIndex;
    if (typeof dataIndex !== "number" || !Number.isFinite(dataIndex)) {
      return p;
    }
    const synthesizedTs =
      timing.lastTs + (dataIndex - timing.lastIndex) * timing.barInterval;
    mutated = true;
    return { ...p, timestamp: synthesizedTs } as T;
  });
  return mutated ? next : [...points];
}

export function adjustOverlayPoints(
  chart: ChartInstance | null | undefined,
  points: DrawingPoint[] | undefined
): OverlayRenderPoint[] | undefined {
  if (!points?.length) return points;
  const timing = readChartTiming(chart);
  if (!timing) return points;

  let mutated = false;
  const next: OverlayRenderPoint[] = points.map((p) => {
    if (
      typeof p.timestamp === "number" &&
      Number.isFinite(p.timestamp) &&
      p.timestamp > timing.lastTs
    ) {
      const futureBars = Math.max(
        1,
        Math.round((p.timestamp - timing.lastTs) / timing.barInterval)
      );
      const { timestamp: _ts, ...rest } = p;
      void _ts;
      mutated = true;
      return { ...rest, dataIndex: timing.lastIndex + futureBars };
    }
    return p as OverlayRenderPoint;
  });
  return mutated ? next : points;
}

/**
 * DR-03: a drawing is "future-anchored" when any of its points sits beyond the
 * chart's last candle. Such overlays are rendered via an extrapolated
 * `dataIndex` (see `adjustOverlayPoints`), which shifts whenever the underlying
 * data list grows — e.g. when older history is prepended on scroll-back. These
 * instances must be re-anchored on every data revision, not just first load.
 */
export function isFutureAnchored(
  chart: ChartInstance | null | undefined,
  points: DrawingPoint[] | undefined
): boolean {
  if (!points?.length) return false;
  const timing = readChartTiming(chart);
  if (!timing) return false;
  return points.some(
    (p) =>
      typeof p.timestamp === "number" &&
      Number.isFinite(p.timestamp) &&
      p.timestamp > timing.lastTs
  );
}

export function lineStyleFromSettings(style: Record<string, unknown> | undefined) {
  const lineStyle = (style?.lineStyle as string) || "solid";
  return {
    color: (style?.color as string) || DEFAULT_DRAWING_COLOR,
    size: (style?.lineWidth as number) || 1,
    style: lineStyle === "dotted" ? "dashed" : lineStyle,
    dashedValue:
      lineStyle === "dotted"
        ? [2, 2]
        : lineStyle === "dashed"
          ? [5, 5]
          : undefined,
  };
}

export function buildKLineLineStyles(style: Record<string, unknown> | undefined) {
  return { line: lineStyleFromSettings(style) };
}

/**
 * Wraps `onPointsChange` so every persisted points payload first passes through
 * `synthesizeFutureTimestamps`. After dragging a point into the future,
 * KLineCharts hands us `{ dataIndex: lastIdx+N, timestamp: undefined }`; without
 * this guard the absolute time anchor would be lost the next time the data list
 * changes (timeframe/symbol switch).
 */
export function pressedMoveEndHandler(
  chart: ChartInstance,
  onPointsChange: OnPointsChange
): (args: { overlay: { id?: string; points?: DrawingPoint[] } }) => void {
  return ({ overlay }) => {
    if (overlay?.id && overlay.points) {
      const stable = synthesizeFutureTimestamps(chart, overlay.points);
      onPointsChange(overlay.id, stable);
    }
  };
}

export function safeCreateOverlay(
  chart: ChartInstance,
  options: Record<string, unknown>,
  label: string
) {
  try {
    chart.createOverlay(options);
  } catch (error) {
    console.error(`Failed to create ${label} overlay:`, error);
  }
}

export function safeOverrideOverlay(
  chart: ChartInstance,
  options: Record<string, unknown>,
  label: string
) {
  try {
    chart.overrideOverlay(options);
  } catch (error) {
    console.error(`Failed to override ${label} overlay:`, error);
  }
}

export function safeRemoveOverlay(chart: ChartInstance, id: string) {
  try {
    chart.removeOverlay(id);
  } catch (error) {
    console.error(`Failed to remove overlay ${id}:`, error);
  }
}

export function loadExtendDataOverlay(
  chart: ChartInstance,
  instance: DrawingInstance,
  overlayName: string,
  minPoints: number,
  onPointsChange: OnPointsChange
) {
  const points = instance.settings?.points;
  if (!points || points.length < minPoints) return;

  safeCreateOverlay(
    chart,
    {
      name: overlayName,
      id: instance.id,
      paneId: instance.windowId,
      points: adjustOverlayPoints(chart, points),
      lock: false,
      visible: instance.visible !== false,
      extendData: { settings: instance.settings },
      onPressedMoveEnd: pressedMoveEndHandler(chart, onPointsChange),
    },
    overlayName
  );
}

export function recreateExtendDataOverlay(
  chart: ChartInstance,
  instance: DrawingInstance,
  overlayName: string,
  onPointsChange: OnPointsChange
) {
  safeRemoveOverlay(chart, instance.id);
  const points = instance.settings?.points ?? [];
  safeCreateOverlay(
    chart,
    {
      name: overlayName,
      id: instance.id,
      paneId: instance.windowId,
      points: adjustOverlayPoints(chart, points),
      lock: false,
      visible: instance.visible !== false,
      extendData: { settings: instance.settings },
      onPressedMoveEnd: pressedMoveEndHandler(chart, onPointsChange),
    },
    overlayName
  );
}

export function updateExtendDataOverlay(
  chart: ChartInstance,
  instance: DrawingInstance
) {
  safeOverrideOverlay(
    chart,
    {
      id: instance.id,
      visible: instance.visible !== false,
      extendData: { settings: instance.settings },
    },
    instance.type
  );
}

export function pointsEqual(
  a: DrawingPoint[] | undefined,
  b: DrawingPoint[] | undefined
): boolean {
  if (!a && !b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every(
    (p, i) =>
      p.timestamp === b[i].timestamp &&
      p.value === b[i].value
  );
}
